import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Clock, Truck, Users, Calculator, ArrowRight } from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Clock,
      title: 'Save Time',
      description: 'No more shopping, washing, or inventory management. Focus on your guests.'
    },
    {
      icon: CheckCircle,
      title: 'Premium Quality',
      description: 'Hotel-grade linens that maintain their quality wash after wash.'
    },
    {
      icon: Truck,
      title: 'Reliable Delivery',
      description: 'Scheduled pickup and delivery to keep your properties always ready.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'Airbnb Host, Chicago',
      content: 'LaundryBnB has been a game-changer for my 3 properties. I save 10+ hours per week and my guests love the quality.',
      properties: '3 properties'
    },
    {
      name: 'Mike Rodriguez',
      role: 'Property Manager, Schaumburg',
      content: 'Managing 15 short-term rentals used to be overwhelming. Now the linen service runs like clockwork.',
      properties: '15 properties'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-white py-24">
        <div className="container mx-auto px-8">
          <div className="max-w-4xl mx-auto text-center py-12">
            <h1 className="display-large text-gray-900 mb-6">
              Premium Linen for
              <span className="text-red-500"> Short-Term Rentals</span>
            </h1>
            <p className="body-large text-gray-600 mb-8 max-w-2xl mx-auto">
              Stop buying, washing, and managing linens. Get hotel-quality bedding and towels 
              delivered clean and ready for your guests. Perfect for Airbnb hosts and property managers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/pricing')}
                className="btn-primary flex items-center justify-center gap-2"
              >
                <Calculator className="w-5 h-5" />
                Get Your Quote
              </button>
              <button className="px-8 py-4 border border-gray-300 rounded-lg font-medium text-gray-600 cursor-default">
                See How It Works
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="display-small text-gray-900 mb-4">
                Why Property Owners Choose LaundryBnB
              </h2>
              <p className="body-large text-gray-600">
                From individual hosts to large property management companies
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="marketplace-card bg-white p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 bg-red-50 rounded-lg flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-red-500" />
                  </div>
                  <h3 className="heading-4 text-gray-900 mb-2">{feature.title}</h3>
                  <p className="body-medium text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Simple Pricing Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="display-small text-gray-900 mb-4">
                Simple, Transparent Pricing
              </h2>
              <p className="body-large text-gray-600">
                Pay monthly rental fees + laundering charges only when you need service
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="text-center mb-6">
                <h3 className="heading-2 text-gray-900 mb-4">How It Works</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-lg">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <span className="text-blue-600 font-bold">1</span>
                      </div>
                      <h4 className="heading-4 text-gray-900 mb-2">Monthly Rental</h4>
                      <p className="body-small text-gray-600">
                        Low monthly rental for hotel quality linen
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <span className="text-green-600 font-bold">2</span>
                      </div>
                      <h4 className="heading-4 text-gray-900 mb-2">Laundry Costs</h4>
                      <p className="body-small text-gray-600">
                        Transparent cleaning and delivery costs, no surprises
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="grid grid-cols-1 gap-4 text-sm text-gray-600">
                  <div>• Volume discounts available for 10+ properties</div>
                  <div>• No upfront costs</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="display-small text-gray-900 mb-4">
                Trusted by Property Owners
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="marketplace-card bg-white p-8">
                  <div className="mb-6">
                    <p className="body-large text-gray-700 italic">
                      "{testimonial.content}"
                    </p>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                      <p className="body-small text-gray-600">{testimonial.role}</p>
                    </div>
                    <div className="text-right">
                      <span className="body-small font-medium text-red-500">
                        {testimonial.properties}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="display-small text-gray-900 mb-4">
              Ready to Simplify Your Linen Management?
            </h2>
            <p className="body-large text-gray-600 mb-8">
              Get a custom quote for your properties in under 5 minutes
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/pricing')}
                className="btn-primary flex items-center justify-center gap-2"
              >
                Start Your Quote
                <ArrowRight className="w-5 h-5" />
              </button>
              <button className="px-8 py-4 border border-gray-300 rounded-lg font-medium text-gray-600 cursor-default">
                Schedule a Call
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;